import {
  createColumnHelper,
  type PaginationState,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';

import type {
  AudiencePreviewReason,
  AudiencePreviewRecipient,
} from '~features/email-builder/model/types';

import {
  EnrollioTable,
  EnrollioTableColumnToggle,
} from '~shared/components/enrollio-table';
import { DataTableColumnHeader } from '~shared/components/table';
import { getStoredPageSize } from '~shared/helpers/page-size-preference';
import { Input } from '~shared/shadcn/ui/input';

interface AudienceOption {
  label: string;
  value: string;
}

interface AudienceRow {
  parentId: string;
  name: string;
  email: string;
  byTag: string;
  byClass: string;
  byEvent: string;
}

const columnHelper = createColumnHelper<AudienceRow>();

const columns = [
  columnHelper.accessor('name', {
    header: (info) => (
      <DataTableColumnHeader
        disableHiding
        column={info.column}
        title='Name'
        className='pl-4 text-black'
      />
    ),
    cell: (info) => (
      <div className='px-4 py-2 font-medium'>{info.getValue()}</div>
    ),
  }),
  columnHelper.accessor('email', {
    header: (info) => (
      <DataTableColumnHeader
        disableHiding
        column={info.column}
        title='Email'
        className='pl-4 text-black'
      />
    ),
    cell: (info) => (
      <div className='px-4 py-2 text-muted-foreground'>{info.getValue()}</div>
    ),
  }),
  columnHelper.accessor('byTag', {
    header: (info) => (
      <DataTableColumnHeader
        column={info.column}
        title='By Tag'
        className='pl-4 text-black'
      />
    ),
    cell: (info) => (
      <div className='px-4 py-2 text-sm'>{info.getValue() || '—'}</div>
    ),
  }),
  columnHelper.accessor('byClass', {
    header: (info) => (
      <DataTableColumnHeader
        column={info.column}
        title='By Class'
        className='pl-4 text-black'
      />
    ),
    cell: (info) => (
      <div className='px-4 py-2 text-sm'>{info.getValue() || '—'}</div>
    ),
  }),
  columnHelper.accessor('byEvent', {
    header: (info) => (
      <DataTableColumnHeader
        column={info.column}
        title='By Event'
        className='pl-4 text-black'
      />
    ),
    cell: (info) => (
      <div className='px-4 py-2 text-sm'>{info.getValue() || '—'}</div>
    ),
  }),
];

const looksLikeDateValue = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }
  // ISO / common locale date strings should never be shown as names.
  if (
    /^\d{4}-\d{2}-\d{2}/.test(trimmed) ||
    /^\d{1,2}\/\d{1,2}\/\d{2,4}/.test(trimmed) ||
    /^\d{1,2}\s+[A-Za-z]{3,9}\s+\d{4}/.test(trimmed) ||
    /^[A-Za-z]{3,9}\s+\d{1,2},?\s+\d{4}/.test(trimmed) ||
    /^[A-Za-z]{3}\s+[A-Za-z]{3}\s+\d{1,2}\s+\d{4}/.test(trimmed)
  ) {
    return true;
  }
  const parsed = Date.parse(trimmed);
  return (
    !Number.isNaN(parsed) &&
    (trimmed.includes('T') ||
      trimmed.includes(':') ||
      trimmed.includes('GMT') ||
      trimmed.includes('UTC'))
  );
};

const getReasonId = (
  reason: AudiencePreviewReason | string | Record<string, unknown>,
) => {
  if (typeof reason === 'string') {
    return looksLikeDateValue(reason) ? '' : String(reason);
  }
  if (reason && typeof reason === 'object') {
    if ('id' in reason && reason.id != null) {
      return String(reason.id);
    }
    if ('_id' in reason && reason._id != null) {
      return String(reason._id);
    }
  }
  return '';
};

const getReasonName = (
  reason: AudiencePreviewReason | string | Record<string, unknown>,
) => {
  if (typeof reason === 'string') {
    return looksLikeDateValue(reason) ? '' : reason;
  }
  if (reason && typeof reason === 'object') {
    const name =
      typeof reason.name === 'string'
        ? reason.name
        : typeof reason.fieldName === 'string'
          ? reason.fieldName
          : typeof reason.label === 'string'
            ? reason.label
            : '';
    if (name && !looksLikeDateValue(name)) {
      return name;
    }
  }
  return '';
};

interface ReasonLookups {
  labelById: Map<string, string>;
  labelByName: Map<string, string>;
}

const createLookups = (options: Array<AudienceOption>): ReasonLookups => {
  const labelById = new Map<string, string>();
  const labelByName = new Map<string, string>();
  for (const option of options) {
    labelById.set(String(option.value), option.label);
    labelByName.set(option.label.toLowerCase(), option.label);
  }
  return { labelById, labelByName };
};

const formatReasonNames = (
  reasons:
    | Array<AudiencePreviewReason>
    | Array<string>
    | Array<Record<string, unknown>>
    | undefined,
  lookups: ReasonLookups,
) => {
  if (!reasons?.length) {
    return '';
  }

  const names = reasons
    .map((reason) => {
      const id = getReasonId(reason);
      const selectedLabel = id ? lookups.labelById.get(id) : undefined;
      // Selected campaign options are the source of truth for display names.
      if (selectedLabel && !looksLikeDateValue(selectedLabel)) {
        return selectedLabel;
      }

      const reasonName = getReasonName(reason);
      if (!reasonName) {
        return '';
      }

      const matchedByLabel = lookups.labelByName.get(reasonName.toLowerCase());
      if (matchedByLabel && !looksLikeDateValue(matchedByLabel)) {
        return matchedByLabel;
      }

      // Never render date-like API fallbacks in these columns.
      return looksLikeDateValue(reasonName) ? '' : reasonName;
    })
    .filter((name): name is string => Boolean(name?.trim()));

  return [...new Set(names)].join(', ');
};

interface AudiencePreviewTableProps {
  recipients: Array<AudiencePreviewRecipient>;
  tagOptions?: Array<AudienceOption>;
  classOptions?: Array<AudienceOption>;
  eventOptions?: Array<AudienceOption>;
}

export const AudiencePreviewTable = ({
  recipients,
  tagOptions = [],
  classOptions = [],
  eventOptions = [],
}: AudiencePreviewTableProps) => {
  const [search, setSearch] = useState('');
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'name', desc: false },
  ]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: getStoredPageSize(),
  });
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  const tagLookups = useMemo(() => createLookups(tagOptions), [tagOptions]);
  const classLookups = useMemo(
    () => createLookups(classOptions),
    [classOptions],
  );
  const eventLookups = useMemo(
    () => createLookups(eventOptions),
    [eventOptions],
  );

  const rows = useMemo<Array<AudienceRow>>(
    () =>
      recipients.map((recipient) => ({
        parentId: recipient.parentId,
        name: recipient.name,
        email: recipient.email,
        byTag: formatReasonNames(recipient.reasons?.tags, tagLookups),
        byClass: formatReasonNames(recipient.reasons?.classes, classLookups),
        byEvent: formatReasonNames(recipient.reasons?.events, eventLookups),
      })),
    [recipients, tagLookups, classLookups, eventLookups],
  );

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return rows;
    }
    return rows.filter((row) => {
      const haystack = [
        row.name,
        row.email,
        row.byTag,
        row.byClass,
        row.byEvent,
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [rows, search]);

  if (rows.length === 0) {
    return (
      <p className='py-6 text-center text-sm text-muted-foreground'>
        No contacts match the selected tags, classes, or events.
      </p>
    );
  }

  return (
    <EnrollioTable
      ariaLabel='Email audience preview'
      totalLabel='Contacts'
      filterHeader={
        <>
          <Input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }}
            placeholder='Search by name, email, tag, class, or event'
            className='w-full md:w-96'
          />
          <p className='text-xs text-muted-foreground'>
            {filteredRows.length} of {rows.length} contacts
          </p>
        </>
      }
      toolbar={(table) => <EnrollioTableColumnToggle table={table} />}
      columns={columns}
      data={filteredRows}
      sorting={sorting}
      onSortingChange={setSorting}
      pagination={pagination}
      onPaginationChange={setPagination}
      columnVisibility={columnVisibility}
      onColumnVisibilityChange={setColumnVisibility}
      getRowId={(row) => row.parentId}
      className='rounded-md border border-border'
      emptyContent='No contacts match your search.'
    />
  );
};
